import type { ChatwootRealtimeEvent } from '@/composables/useChatwootRealtime'
import type { Conversation } from '@/types/dashboard'
import type { ChatMessage } from '@/types/chat'

// ── Fase 2/3 do plano de tempo real (docs/planos/PLANO-REALTIME-ACTIONCABLE.md) ──
// Tradução PURA de evento bruto do ActionCable → patch de conversa. Não toca
// em conversations.value nem em nenhum estado reativo — quem aplica o patch é
// applyRealtimePatch() em useDashboardEngine.ts. Sem IO, só transformação.
//
// Dois formatos de evento confirmados ao vivo, com formas BEM diferentes:
//
// 1) message.created/message.updated — só a mensagem em si, com um
//    sub-objeto "conversation" parcial (assignee_id como NÚMERO, unread_count,
//    last_activity_at). Não dá pra montar um Assignee completo nem uma
//    Conversation inteira só com isso → patch "patch" (campos soltos).
//
// 2) conversation.updated/assignee.changed — a conversa INTEIRA, no mesmo
//    formato bruto que o Chatwoot usa nativamente (confirmado batendo com o
//    que parse() já espera de conv.meta.sender/assignee/team, exceto por
//    "channel", que aqui vem solto em cima, não dentro de meta — diferença
//    real em relação ao formato que o n8n devolve, não bug de digitação).
//    Isso dá o suficiente pra montar uma Conversation completa de verdade,
//    incluindo o Assignee inteiro (nome/thumbnail), sem precisar cruzar com
//    allAgents.value → patch "snapshot".
//
// presence.update (confirmado na Fase 1) continua caindo em null de
// propósito — não é sobre uma conversa específica. Conversa CRIADA ainda não
// foi capturada — qualquer evento não mapeado devolve null (não é bug).

export interface ConversationPatchFields {
  assigneeId?: number | null
  unreadCount?: number
  lastActivityAt?: number
}

export interface ConversationEventPatch {
  kind: 'patch'
  conversationId: number
  fields: ConversationPatchFields
}

export interface ConversationSnapshotPatch {
  kind: 'snapshot'
  conversationId: number
  conversation: Conversation
}

export type TranslatedEvent = ConversationEventPatch | ConversationSnapshotPatch | null

interface RawMessageEventData {
  conversation_id?: number
  conversation?: {
    assignee_id?: number | null
    unread_count?: number
    last_activity_at?: number
  }
}

function translateMessageEvent(data: unknown): TranslatedEvent {
  const d = data as RawMessageEventData
  if (!d || typeof d.conversation_id !== 'number') return null

  const conv = d.conversation
  const fields: ConversationPatchFields = {}
  if (conv) {
    if ('assignee_id' in conv) fields.assigneeId = conv.assignee_id ?? null
    if (typeof conv.unread_count === 'number') fields.unreadCount = conv.unread_count
    if (typeof conv.last_activity_at === 'number') fields.lastActivityAt = conv.last_activity_at
  }

  // Sem nenhum campo aproveitável (payload incompleto/inesperado) — não
  // vale a pena devolver um patch vazio, quem consumir teria que checar
  // isso de qualquer forma.
  if (Object.keys(fields).length === 0) return null

  return { kind: 'patch', conversationId: d.conversation_id, fields }
}

// Mesmo formato bruto que parse() (useDashboardEngine.ts) já espera de
// conv.meta.sender/assignee/team — a diferença confirmada é só "channel",
// que aqui vem solto (não em meta). Mapeamento deliberadamente espelha
// parse() pra não divergir em duas implementações (mesmo cuidado já tomado
// com o motor de macros, ver CLAUDE.md).
export interface RawChatwootConversation {
  id?: number
  status?: string
  channel?: string
  waiting_since?: number
  unread_count?: number
  created_at?: number
  updated_at?: number
  resolved_at?: number
  snoozed_until?: number | null
  labels?: string[]
  priority?: 'urgent' | 'high' | 'medium' | 'low' | 'none' | null
  custom_attributes?: { fluxo_id?: number; cpf?: string; cnpj?: string }
  meta?: {
    sender?: { id?: number; name?: string; phone_number?: string; email?: string; thumbnail?: string }
    assignee?: {
      id?: number
      name?: string
      available_name?: string
      email?: string
      role?: string
      thumbnail?: string
      availability_status?: string
    } | null
    team?: { id?: number; name?: string } | null
  }
}

// Extraído pra ser reaproveitado também pela Fase 6 (busca pontual de UMA
// conversa via REST, services/conversationService.ts) — mesmo formato bruto
// nativo do Chatwoot em ambos os casos (evento de WebSocket e GET
// /conversations/:id), então não vale duplicar o mapeamento.
export function mapRawConversation(conv: RawChatwootConversation): Conversation {
  const m = conv.meta || {}

  const conversation: Conversation = {
    id:            conv.id ?? 0, // callers já garantem id numérico antes de chamar (ver checks em translateConversationSnapshotEvent/conversationService.ts)
    status:        conv.status || 'open',
    channel:       conv.channel || '',
    waiting_since: conv.waiting_since || 0,
    unread_count:  conv.unread_count  || 0,
    created_at:    conv.created_at    || 0,
    resolved_at:   conv.resolved_at   || (conv.status === 'resolved' ? (conv.updated_at || 0) : 0),
    snoozed_until: conv.snoozed_until ?? null,
    contact: {
      id:        m.sender?.id            || 0,
      name:      m.sender?.name         || '—',
      phone:     m.sender?.phone_number || m.sender?.email || '',
      thumbnail: m.sender?.thumbnail    || '',
    },
    assignee: m.assignee ? {
      id:           m.assignee.id ?? 0,
      name:         m.assignee.name || m.assignee.available_name || '—',
      email:        m.assignee.email || '',
      role:         m.assignee.role  || 'agent',
      thumbnail:    m.assignee.thumbnail || '',
      availability: m.assignee.availability_status || 'offline',
    } : null,
    team: m.team ? {
      id:   m.team.id ?? 0,
      name: m.team.name || `Time #${m.team.id}`,
    } : null,
    labels: Array.isArray(conv.labels) ? conv.labels : [],
    fluxo_id: conv.custom_attributes?.fluxo_id ?? 1,
    priority: conv.priority || null,
    cpf:  conv.custom_attributes?.cpf  || null,
    cnpj: conv.custom_attributes?.cnpj || null,
  }

  return conversation
}

function translateConversationSnapshotEvent(data: unknown): TranslatedEvent {
  const conv = data as RawChatwootConversation
  if (!conv || typeof conv.id !== 'number') return null
  return { kind: 'snapshot', conversationId: conv.id, conversation: mapRawConversation(conv) }
}

// ── Fase 5 — mensagens em tempo real na conversa aberta ──────────────────
// Tradução INDEPENDENTE de translateChatwootEvent() acima: mesmo evento
// (message.created/message.updated), extraindo o conteúdo da mensagem em si
// em vez do subconjunto de campos da conversa. As duas convivem — cada
// consumidor de onChatwootEvent() escolhe qual tradutor chamar; não há
// conflito, o evento bruto é o mesmo, só a leitura muda.
interface RawChatwootMessage {
  id?: number
  content?: string | null
  message_type?: number
  created_at?: number
  attachments?: unknown
  sender?: { name?: string; available_name?: string; type?: string; thumbnail?: string } | null
  status?: string
  private?: boolean
  conversation_id?: number
  // Mesmo sub-objeto parcial documentado no topo do arquivo pra
  // translateMessageEvent() — reaproveitado aqui só pra saber o assignee
  // sem duplicar outra leitura do payload bruto.
  conversation?: { assignee_id?: number | null }
}

export interface MessageEvent {
  conversationId: number
  message: ChatMessage
  /** Assignee da conversa no momento do evento — undefined se o payload não trouxe o campo, null se a conversa não tem assignee. */
  assigneeId?: number | null
}

export function translateMessageContent(evt: ChatwootRealtimeEvent): MessageEvent | null {
  if (evt.event !== 'message.created' && evt.event !== 'message.updated') return null

  const d = evt.data as RawChatwootMessage
  if (!d || typeof d.id !== 'number' || typeof d.conversation_id !== 'number') return null

  const message: ChatMessage = {
    id:           d.id,
    content:      d.content ?? null,
    message_type: d.message_type ?? 0,
    created_at:   d.created_at ?? 0,
    attachments:  Array.isArray(d.attachments) ? d.attachments as ChatMessage['attachments'] : undefined,
    sender: d.sender ? {
      name:           d.sender.name,
      available_name: d.sender.available_name,
      type:           d.sender.type,
      thumbnail:      d.sender.thumbnail,
    } : undefined,
    status:  d.status,
    private: d.private,
  }

  return {
    conversationId: d.conversation_id,
    message,
    assigneeId: d.conversation && 'assignee_id' in d.conversation ? d.conversation.assignee_id ?? null : undefined,
  }
}

export function translateChatwootEvent(evt: ChatwootRealtimeEvent): TranslatedEvent {
  switch (evt.event) {
    case 'message.created':
    case 'message.updated':
      return translateMessageEvent(evt.data)
    case 'conversation.updated':
    case 'assignee.changed':
      return translateConversationSnapshotEvent(evt.data)
    default:
      return null
  }
}
